import _ from 'lodash-es';
import moment from 'moment';
// @ts-ignore
import * as jsonLogic from 'json-logic-js/logic';
// @ts-ignore
import { conformToMask } from 'vanilla-text-mask';
// eslint-disable-next-line import/no-cycle
import {
	checkJsonConditional,
	dataChanged,
} from '~/components/kform/kform.controller';
import Card from '~/scripts/services/model/card';

const optionsLabelOnTheTopOrBottom = (/** @type {string} */ position) => ['top', 'bottom'].includes(position);

/** @type {any} */
const cacheData = {};

/** @type {any} */
const hooks = {};

// @ts-ignore
// @ts-ignore
const kformUtils = {
	/** @type {import('../kform.controller').default | undefined} */
	_kform: undefined,

	/**
	 * @param {any} component
	 * @param {any} submission
	 * @param {any} rowData
	 */
	checkCalculated(component, submission, rowData) {
		// Process calculated value stuff if present.
		if (component.calculateValue) {
			_.set(rowData, component.key, this.evaluate(component.calculateValue, {
				value: undefined,
				data: submission ? submission.data : rowData,
				row: rowData,
				util: this,
				component,
			}, 'value'));
		}
	},

	/**
	 * Checks the conditions for a provided component and data.
	 *
	 * @param {any} component The component to check for the condition.
	 * @param {number} row The data within a row
	 * @param {any} data The full submission data.
	 * @param {import('~/scripts/services/model/form').default} form The form containing components
	 * @returns {boolean} Checked result
	 */
	checkCondition(component, row, data, form) {
		if (component.customConditional) {
			return this.checkCustomConditional(component, component.customConditional, row, data, form, 'show');
		}
		if (component.conditional && component.conditional.when) {
			return this.checkSimpleConditional(component, component.conditional, row, data);
		}
		if (component.conditional && component.conditional.json) {
			return checkJsonConditional(component, component.conditional.json, row, data, form);
		}

		// Default to show.
		return true;
	},

	/**
	 * Check if a simple conditional evaluates to true.
	 *
	 * @param {any} component The component to check for the condition.
	 * @param {any} condition
	 * @param {any} row
	 * @param {any} data
	 * @returns {boolean}
	 */
	checkSimpleConditional(component, condition, row, data) {
		/** @type {any} */
		let value = null;
		if (row) {
			value = this.getValue({ data: row }, condition.when);
		}
		if (data && _.isNil(value)) {
			value = this.getValue({ data }, condition.when);
		}
		// FOR-400 - Fix issue where falsey values were being evaluated as show=true
		if (_.isNil(value)) {
			value = '';
		}

		const eq = String(condition.eq);
		const
			show = String(condition.show);

		// Special check for selectboxes component.
		if (_.isObject(value) && _.has(value, condition.eq)) {
			// @ts-ignore
			return String(value[condition.eq]) === show;
		}
		// FOR-179 - Check for multiple values.
		if (value && Array.isArray(value) && value.map(String).includes(eq)) {
			return show === 'true';
		}

		return (String(value) === eq) === (show === 'true');
	},

	/**
	 * Check custom javascript conditional.
	 *
	 * @param {any} component The component to check for the condition.
	 * @param {any} custom
	 * @param {any} row
	 * @param {any} data
	 * @param {any} form
	 * @param {any} variable
	 * @param {?Function} [onError]
	 * @returns {any}
	 */
	checkCustomConditional(component, custom, row, data, form, variable, onError = null) {
		let exec = false;
		if (!(component.key in form.cacheKeys)) {
			form.cacheKeys[component.key] = true;
			for (const r of custom.matchAll(/data.(\w+)/g)) {
				if (!(r[1] in cacheData)) {
					cacheData[r[1]] = [];
				}
				if (!cacheData[r[1]].includes(component.key)) {
					cacheData[r[1]].push(component.key);
				}
			}
			exec = true;
		}

		/** @type {any[]} */
		const keysToRemove = [];

		for (const key of dataChanged) {
			if (key in cacheData) {
				exec = true;
				keysToRemove.push(key);
			}
		}
		// eslint-disable-next-line no-underscore-dangle
		if (!exec || form.kid !== this._kform?.currentClassForm) {
			return form.cacheKeys[component.key];
		}
		// eslint-disable-next-line no-underscore-dangle
		this._kform?.$timeout(() => {
			let index = dataChanged.length - 1;
			while (index >= 0) {
				if (keysToRemove.includes(dataChanged[index])) {
					dataChanged.splice(index, 1);
				}
				index--;
			}
		});

		if (typeof custom === 'string') {
			// eslint-disable-next-line no-param-reassign
			custom = `var ${variable} = true; ${custom}; return ${variable};`;
		}
		form.getValue = (/** @type {string} */ key) => (data && key in data && data[key]) || null;
		const value = this.evaluate(custom, { row, data, form });
		form.cacheKeys[component.key] = value;
		if (value === null && onError) {
			return onError;
		}

		return value;
	},

	/**
	 * @param {any} component
	 * @param {any} submission
	 * @param {any} data
	 * @param {any} $scope
	 * @param {any} [done]
	 * @returns {any}
	 */
	checkDefaultValue(component, submission, data, $scope, done = undefined) {
		/* eslint-disable max-depth */
		let value = '';
		if (!done) {
			// eslint-disable-next-line no-param-reassign
			done = (/** @type {any} */ added) => added;
		}

		if (component.validate?.card && (
			typeof component.validate?.card === 'string' || !(component.validate?.card instanceof Card))
		) {
			component.validate.card = new Card(component.validate.card);
		}

		// Use the current data or default.
		if (Object.prototype.hasOwnProperty.call(data, component.key)) {
			if (!component.validate?.card?.isMultiple()) {
				return done(false);
			}

			// If the value is an array then we are good.
			if (Array.isArray(data[component.key])) {
				return done(false);
			}

			// Split the value based on CSV
			if (typeof data[component.key] === 'string') {
				data[component.key] = data[component.key].split(',');
			}
			return done(true);
		}
		if (Object.prototype.hasOwnProperty.call(component, 'customDefaultValue')) {
			if (typeof component.customDefaultValue === 'string') {
				try {
					// eslint-disable-next-line no-eval
					// noinspection JSUnusedLocalSymbols
					value = eval(`(function(data) { var value = "";${ component.customDefaultValue }; return value; })(data)`);
				}
				catch (error) {
					/* eslint-disable no-console */
					console.warn(`An error occurrend in a custom default value in ${ component.key}`, error);
					/* eslint-enable no-console */
					value = '';
				}
			}
			// else {
			// 	try {
			// 		value = formioUtils.jsonLogic.apply(component.customDefaultValue, {
			// 			data: submission ? submission.data : data,
			// 			row: data,
			// 		});
			// 	}
			// 	catch (error) {
			// 		/* eslint-disable no-console */
			// 		console.warn(`An error occurred calculating a value for ${ component.key}`, error);
			// 		/* eslint-enable no-console */
			// 		value = '';
			// 	}
			// }
			data[component.key] = component.validate?.card?.isMultiple() ? [value] : value;
			return done(true);
		}

		if (Object.prototype.hasOwnProperty.call(component, 'defaultValue')) {
			// FA-835 - The default values for select boxes are set in the component.
			if (component.type === 'selectboxes') {
				return done(false);
			}
			// If there is a default value and it is not an array, wrap the value.
			value = component.validate?.card?.isMultiple()
				&& component.type !== 'file'
				&& typeof component.defaultValue === 'string'
				? component.defaultValue.split(',')
				: component.defaultValue;

			// If no default is provided, then skip...
			if (!value || value.length === 0) {
				return done(false);
			}

			// FOR-193 - Fix default value for the number component.
			// FOR-262 - Fix multiple default value for the number component.
			if (component.type === 'number') {
				if (!component.validate?.card?.isMultiple()) {
					// FOR-290 - Fix default values for number components, to allow decimal numbers.
					if (component.defaultValue.includes('.')) {
						data[component.key] = Number.parseFloat(component.defaultValue);
						return done(true);
					}

					data[component.key] = Number.parseInt(component.defaultValue, 10);
					return done(true);
				}

				// @ts-ignore
				data[component.key] = value.map(item => {
					try {
						// FOR-290 - Fix default values for number components, to allow decimal numbers.
						if (item.includes('.')) {
							return Number.parseFloat(item);
						}

						return Number.parseInt(item, 10);
					}
					catch {
						return 0;
					}
				});
				return done(true);
			}
			// FOR-135 - Add default values for select components.
			if (component.type === 'select') {
				// FOR-337 - Fix default values for select components without multi enabled.
				if (!component.validate?.card?.isMultiple()) {
					data[component.key] = component.defaultValue;
					return done(true);
				}

				// If using the values input, split the default values, and search the options for each value in the list.
				if (component.dataSrc === 'values') {
					const temporary = [];

					for (const item of component.data.values) {
						if (value.includes(item.value)) {
							temporary.push(item.value);
						}
					}

					data[component.key] = temporary;
					return done(true);
				}
				// If using json input, split the values and search each key path for the item
				if (component.dataSrc === 'json') {
					if (typeof component.data.json === 'string') {
						try {
							component.data.json = JSON.parse(component.data.json);
						}
						catch (error) {
							/* eslint-disable no-console */
							console.log(error);
							console.log(`Could not parse the given JSON for the select component: ${ component.key}`);
							console.log(component.data.json);
							/* eslint-enable no-console */
							component.data.json = [];
						}
					}

					data[component.key] = this.pluckItems(value, component.data.json);
					return done(true);
				}
				if (component.dataSrc === 'url' || component.dataSrc === 'resource') {
					// Wait until loading is done.
					$scope.$on('selectLoaded', () => {
						data[component.key] = this.pluckItems(value, $scope.selectItems);
						return done(true);
					});
				}
			}
			// FOR-504 - Fix default values for survey components.
			else if (component.type === 'survey') {
				if (!Object.prototype.hasOwnProperty.call(component, 'defaultValue')) {
					return done(false);
				}

				data[component.key] = data[component.key] || {};
				this.each(component.questions, (/** @type {any} */ question, /** @type {Function} */ next) => {
					// eslint-disable-next-line angular/timeout-service
					setTimeout(() => {
						data[component.key][question.value] = data[component.key][question.value] || component.defaultValue;
						return next();
					}, 1);
				}, () => {
					done(true);
				});
			}
			// FOR-949 - Default value for componenets with input mask.
			else if (component.inputMask) {
				const inputMask = this.getInputMask(component.inputMask);
				value = component.validate?.card?.isMultiple()
					// @ts-ignore
					? value.map(item => this.verifyMaskedInput(item, inputMask))
					: this.verifyMaskedInput(value, inputMask);
				data[component.key] = value;
				return done(true);
			}
			else {
				if (!component.multiple) {
					data[component.key] = component.defaultValue;
					return done(true);
				}

				// If there is a default value and it is an array, assign it to the value.
				if (Array.isArray(component.defaultValue)) {
					data[component.key] = component.defaultValue;
					return done(true);
				}

				// Make the defaultValue a single element array because were multi.
				data[component.key] = [component.defaultValue];
				return done(true);
			}
		}
		/* eslint-enable max-depth */
		return undefined;
	},

	/**
	 * @param {any} component
	 * @param {any} row
	 * @param {any} data
	 * @param {import('~/scripts/services/model/form').default} form
	 * @returns {boolean}
	 */
	checkVisible(component, row, data, form) {
		const visible = this.checkCondition(component, row, data, form);
		if (!visible) {
			if (!('clearOnHide' in component) || component.clearOnHide.toString() === 'true') {
				if (row && component.key in row) {
					delete row[component.key];
				}
				if (data && component.key in data) {
					delete data[component.key];
				}
			}
			return false;
		}
		return true;
	},

	/**
	 * @param {any[]} items
	 * @param {Function} each
	 * @param {?Function} done
	 */
	each(items, each, done) {
		let index = -1;
		const arrayItems = [];
		for (const index2 in items) {
			if (Object.prototype.hasOwnProperty.call(items, index2)) {
				arrayItems.push(items[index2]);
			}
		}

		(function next(/** @type {any} */ error, previous) {
			if (error) {
				if (typeof error === 'string') {
					// eslint-disable-next-line no-param-reassign
					error = { alerts: { type: 'danger', message: `{"data": "${ error }"}` } };
				}
				error.item = previous;
				return done ? done(error) : null;
			}
			index++;
			const item = arrayItems[index];
			if (!item) {
				return done ? done() : null;
			}
			each(item, (/** @type {any} */ error_) => {
				next(error_, item);
			});
			return undefined;
		}());
	},
	/**
	 * Iterate through each component within a form.
	 *
	 * @param {any} components The components to iterate.
	 * @param {Function} function_ The iteration function to invoke for each component.
	 * @param {boolean} includeAll Whether or not to include layout components.
	 * @param {string | undefined} [path] The current data path of the element. Example: data.user.firstName
	 * @param {any} parent The parent object.
	 */
	eachComponent(components, function_, includeAll = false, path = undefined, parent = undefined) {
		if (!components) {
			return;
		}
		// eslint-disable-next-line no-param-reassign
		path = path || '';
		for (const component of components) {
			if (!component) {
				// eslint-disable-next-line no-continue
				continue;
			}
			const hasColumns = component.columns && Array.isArray(component.columns);
			const hasRows = component.rows && Array.isArray(component.rows);
			const hasTabs = component.tabs && Array.isArray(component.tabs);
			const hasComps = component.components && Array.isArray(component.components);
			let noRecurse = false;
			const newPath = component.key ? (path ? (`${path}.${component.key}`) : component.key) : '';

			// Keep track of parent references.
			if (parent) {
				// Ensure we don't create infinite JSON structures.
				component.parent = _.clone(parent);
				delete component.parent.components;
				delete component.parent.componentMap;
				delete component.parent.columns;
				delete component.parent.rows;
				delete component.parent.tabs;
			}

			if (includeAll || component.tree || (!hasColumns && !hasRows && !hasTabs && !hasComps)) {
				noRecurse = function_(component, newPath);
			}

			const subPath = () => {
				if (
					component.key
					&& !['panel', 'table', 'well', 'columns', 'fieldset', 'tabs', 'form'].includes(component.type)
					&& (
						['datagrid', 'container', 'editgrid'].includes(component.type)
						|| component.tree
					)
				) {
					return newPath;
				}
				if (
					component.key
					&& component.type === 'form'
				) {
					return `${newPath}.data`;
				}
				return path;
			};

			if (!noRecurse) {
				if (hasColumns) {
					for (const column of component.columns) {
						this.eachComponent(column.components, function_, includeAll, subPath(), parent ? component : null);
					}
				}

				else if (hasTabs) {
					for (const tab of component.tabs) {
						this.eachComponent(tab.components, function_, includeAll, subPath(), parent ? component : null);
					}
				}

				else if (hasRows) {
					for (const row of component.rows) {
						if (Array.isArray(row)) {
							for (const column of row) {
								this.eachComponent(column.components, function_, includeAll, subPath(), parent ? component : null);
							}
						}
					}
				}

				else if (hasComps) {
					this.eachComponent(component.components, function_, includeAll, subPath(), parent ? component : null);
				}
			}
		}
	},

	/* eslint-disable max-statements */
	/**
	 * Evaluate a method.
	 *
	 * @param {string | Function | false} function_
	 * @param {any} arguments_
	 * @param {string} [returnValue]
	 * @param {any} [tokenize]
	 * @returns {*}
	 */
	evaluate(function_, arguments_, returnValue = undefined, tokenize = undefined) {
		let returnValue2 = null;
		arguments_.component = arguments_.component ? _.cloneDeep(arguments_.component) : { key: 'unknown' };
		if (!arguments_.form && arguments_.instance) {
			arguments_.form = _.get(arguments_.instance, 'root._form', {});
		}

		// Deeply cloning the form is expensive - only do it if it looks like the function needs it
		if (function_.toString().includes('form')) {
			arguments_.form = _.cloneDeep(arguments_.form);
		}
		else {
			delete arguments_.form;
		}

		const componentKey = arguments_.component.key;
		if (typeof function_ === 'string') {
			if (returnValue) {
				// eslint-disable-next-line no-param-reassign
				function_ += `;return ${returnValue}`;
			}
			const parameters = _.keys(arguments_);

			if (tokenize) {
				// Replace all {{ }} references with actual data.
				// eslint-disable-next-line no-param-reassign
				function_ = function_.replace(/({{\s+(.*)\s+}})/, (match, $1, $2) => {
					if ($2.indexOf('data.') === 0) {
						return _.get(arguments_.data, $2.replace('data.', ''));
					}
					if ($2.indexOf('row.') === 0) {
						return _.get(arguments_.row, $2.replace('row.', ''));
					}

					// Support legacy...
					return _.get(arguments_.data, $2);
				});
			}

			try {
				// eslint-disable-next-line no-param-reassign,no-new-func
				function_ = new Function(...parameters, function_);
				// eslint-disable-next-line no-param-reassign
				arguments_ = _.values(arguments_);
			}
			catch (error) {
				console.warn(`An error occured within the custom function for ${componentKey}`, error);
				returnValue2 = null;
				// eslint-disable-next-line no-param-reassign
				function_ = false;
			}
		}
		if (typeof function_ === 'function') {
			try {
				returnValue2 = Array.isArray(arguments_) ? function_(...arguments_) : function_(arguments_);
			}
			catch (error) {
				returnValue2 = null;
				console.warn(`An error occured within custom function for ${componentKey}`, error);
			}
		}
		// else if (typeof function_ === 'object') {
		// 	try {
		// 		returnValue2 = jsonLogic.apply(function_, arguments_);
		// 	}
		// 	catch (error) {
		// 		returnValue2 = null;
		// 		console.warn(`An error occured within custom function for ${componentKey}`, error);
		// 	}
		// }
		else if (function_) {
			console.warn(`Unknown function type for ${componentKey}`);
		}
		return returnValue2;
	},

	/**
	 * @param {string} input
	 * @returns {string}
	 */
	fieldWrap(input) {
		const multiInput = input.replace('data[component.key]', 'data[component.key][$index]');

		const inputTopLabel = '<label ng-if="$parent.labelVisible() && (component.labelPosition !== \'bottom\')" '
			+ 'for="{{ component.key }}"  id="{{ component.key+\'Label\' }}" class="control-label" '
			+ 'ng-class="{\'field-required\': isRequired(component)}" ng-style="$parent.getLabelStyles(component)">'
			+ '{{ component.label }} '
			+ '<kform-component-tooltip></kform-component-tooltip>'
			+ '</label>';

		const inputBottomLabel = '<label ng-if="$parent.labelVisible() && (component.labelPosition === \'bottom\')" '
			+ 'id="{{ component.key+\'Label\' }}" for="{{ component.key }}" class="control-label control-label--bottom" '
			+ 'ng-class="{\'field-required\': $parent.isRequired(component)}">'
			+ '{{ component.label }} '
			+ '<kform-component-tooltip></kform-component-tooltip>'
			+ '</label>';

		const requiredInline = '<span ng-if="(component.hideLabel === true || component.label === \'\' || '
			+ '!component.label) && $parent.isRequired(component)" class="glyphicon glyphicon-asterisk form-control-feedback '
			+ 'field-required-inline" aria-hidden="true"></span>';

		return `<div ng-if="!component.multiple">${
			inputTopLabel
		}<div class="input-group input-group-sm" ng-style="$parent.getInputGroupStyles(component)">`
			+ `<div class="input-group-addon" ng-if="!!component.prefix">{{ component.prefix }}</div>${
				input
			}${requiredInline
			}<div class="input-group-addon" ng-if="!!component.suffix">{{ component.suffix }}</div>`
			+ `</div>${
				inputBottomLabel
			}<div class="kform-errors">`
			+ '<kform-errors ng-if="::!options.building"></kform-errors>'
			+ '</div>'
			+ '</div>'
			+ `<div ng-if="component.multiple">${
				inputTopLabel
			}<table class="table table-bordered" ng-style="$parent.getInputGroupStyles(component)">`
			+ '<tr ng-repeat="value in data[component.key] track by $index">'
			+ '<td>'
			+ '<div class="input-group">'
			+ `<div class="input-group-addon" ng-if="!!component.prefix">{{ component.prefix }}</div>${
				multiInput
			}${requiredInline
			}<div class="input-group-addon" ng-if="!!component.suffix">{{ component.suffix }}</div>`
			+ '</div>'
			+ '<div class="kform-errors">'
			+ '<kform-errors ng-if="::!options.building"></kform-errors>'
			+ '</div>'
			+ '</td>'
			+ '<td><a ng-click="(readOnly || kformForm.submitting)? null:$parent.removeFieldValue($index) " '
			+ 'ng-disabled = "readOnly || kformForm.submitting" class="btn btn-default"><span '
			+ 'class="glyphicon glyphicon-remove-circle"></span></a></td>'
			+ '</tr>'
			+ '<tr>'
			+ '<td colspan="2"><a ng-click="(readOnly || kformForm.submitting)? null: $parent.addFieldValue() " '
			+ 'ng-disabled = "readOnly || kformForm.submitting" class="btn btn-primary"><span '
			+ 'class="glyphicon glyphicon-plus" aria-hidden="true"></span> '
			+ '{{ component.addAnother || "form.add-another" | translate }}</a></td>'
			+ '</tr>'
			+ `</table>${
				inputBottomLabel
			}</div>`
			+ '<div ng-if="!!component.description" class="help-block">'
			+ '<span id="{{ component.key + \'Desc\' }}" ng-if="!isDescriptionHTML()">{{ component.description }}</span>'
			+ '<div class="well" id="{{ component.key + \'Desc\' }}" ng-if="isDescriptionHTML()"'
			+ 'ng-bind-html="component.description"></div>'
			+ '</div>';
	},

	/**
	 * Flatten the form components for data manipulation.
	 *
	 * @param {Object} components The components to iterate.
	 * @param {boolean} [includeAll] Whether or not to include layout components.
	 * @returns {any} The flattened components map.
	 */
	flattenComponents(components, includeAll = false) {
		/** @type {any} */
		const flattened = {};
		this.eachComponent(components, (/** @type {any} */ component, /** @type {string} */ path) => {
			flattened[path] = component;
		}, includeAll);
		return flattened;
	},

	/**
	 * @param {any} number
	 * @param {any} mask
	 * @returns {any}
	 */
	formatNumber(number, mask) {
		// eslint-disable-next-line no-param-reassign
		number = (number || 0).toString();
		return conformToMask(number, mask(number).filter((/** @type {string} */ item) => item !== '[]')).conformedValue;
	},
	/**
	 * Get a component by its key
	 *
	 * @param {any} components The components to iterate.
	 * @param {string} key The key of the component to get, or a query of the component to search.
	 * @param {boolean} includeAll Include all?
	 * @returns {any} The component that matches the given key, or undefined if not found.
	 */
	getComponent(components, key, includeAll) {
		let result;
		this.eachComponent(components, (/** @type {any} */ component, /** @type {string} */ path) => {
			if (path === key) {
				component.path = path;
				result = component;
				return true;
			}
			return false;
		}, includeAll);
		return result;
	},

	/**
	 * Return a translated date setting.
	 *
	 * @param {*} date
	 * @returns {(null|Date)} Date
	 */
	getDateSetting(date) {
		if (_.isNil(date) || _.isNaN(date) || date === '') {
			return null;
		}

		if (date instanceof Date) {
			return date;
		}

		if (typeof date.toDate === 'function') {
			return date.isValid() ? date.toDate() : null;
		}

		let dateSetting = ((typeof date !== 'string') || (!date.includes('moment('))) ? moment(date) : null;
		if (dateSetting && dateSetting.isValid()) {
			return dateSetting.toDate();
		}

		dateSetting = null;
		try {
			// eslint-disable-next-line no-new-func
			const value = (new Function('moment', `return ${date};`))(moment);
			if (typeof value === 'string') {
				dateSetting = moment(value);
			}
			else if (typeof value.toDate === 'function') {
				dateSetting = moment(value.toDate().toUTCString());
			}
			else if (value instanceof Date) {
				dateSetting = moment(value);
			}
		}
		catch {
			return null;
		}

		if (!dateSetting) {
			return null;
		}

		// Ensure this is a date.
		if (!dateSetting.isValid()) {
			return null;
		}

		return dateSetting.toDate();
	},

	/**
	 * @param {any} mask
	 * @returns {RegExp[]}
	 */
	getInputMask(mask) {
		if (Array.isArray(mask)) {
			return mask;
		}
		/** @type {RegExp[]} */
		const maskArray = [];
		// @ts-ignore
		maskArray.numeric = true;
		for (const element of mask) {
			switch (element) {
			case '9': {
				maskArray.push(/\d/);
				break;
			}
			case 'A': {
				// @ts-ignore
				maskArray.numeric = false;
				maskArray.push(/[A-Za-z]/);
				break;
			}
			case 'a': {
				// @ts-ignore
				maskArray.numeric = false;
				maskArray.push(/[a-z]/);
				break;
			}
			case '*': {
				// @ts-ignore
				maskArray.numeric = false;
				maskArray.push(/[\dA-Za-z]/);
				break;
			}
			default: {
				maskArray.push(element);
				break;
			}
			}
		}
		return maskArray;
	},

	/**
	 * @param {any} component
	 * @returns {number}
	 */
	getNumberDecimalLimit(component) {
		// Determine the decimal limit. Defaults to 20 but can be overridden by validate.step or decimalLimit settings.
		let decimalLimit = 20;
		const step = _.get(component, 'validate.step', 'any');

		if (step !== 'any') {
			const parts = step.toString().split('.');
			if (parts.length > 1) {
				decimalLimit = parts[1].length;
			}
		}

		return decimalLimit;
	},

	/**
	 * @param {string} [lang]
	 * @returns {{decimalSeparator: string, delimiter: string}}
	 */
	getNumberSeparators(lang = 'en') {
		const formattedNumberString = (12_345.6789).toLocaleString(lang);
		const delimeters = formattedNumberString.match(/..(.)...(.)../);
		if (!delimeters) {
			return {
				delimiter: ',',
				decimalSeparator: '.',
			};
		}
		return {
			delimiter: (delimeters.length > 1) ? delimeters[1] : ',',
			decimalSeparator: (delimeters.length > 2) ? delimeters[2] : '.',
		};
	},

	/**
	 * @param {string} position
	 * @returns {any}
	 */
	getOptionInputStyles(position) {
		if (position === 'left') {
			return {
				position: 'initial',
				'margin-left': 0,
			};
		}

		if (optionsLabelOnTheTopOrBottom(position)) {
			return {
				width: '100%',
				position: 'initial',
				'margin-left': 0,
			};
		}

		return null;
	},

	/**
	 * @param {string} position
	 * @returns {any}
	 */
	getOptionLabelStyles(position) {
		if (position === 'left') {
			return {
				'text-align': 'center',
				'padding-left': 0,
			};
		}

		if (optionsLabelOnTheTopOrBottom(position)) {
			return {
				display: 'block',
				'text-align': 'center',
				'padding-left': 0,
			};
		}

		return null;
	},

	/**
	 * Get the value for a component key, in the given submission.
	 *
	 * @param {any} submission A submission object to search.
	 * @param {string} key A for components API key to search for.
	 * @returns {*} Value
	 */
	getValue(submission, key) {
		const search = (/** @type {any} */ data) => {
			if (_.isPlainObject(data)) {
				if (_.has(data, key)) {
					return data[key];
				}

				let value = null;

				// eslint-disable-next-line consistent-return
				_.forOwn(data, property => {
					const result = search(property);
					if (!_.isNil(result)) {
						value = result;
						return false;
					}
				});

				return value;
			}

			return null;
		};

		return search(submission.data);
	},

	guid() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, c => {
			const r = Math.trunc(Math.random() * 16); const
				// eslint-disable-next-line no-mixed-operators
				v = c === 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	},

	/**
	 * @param {string} name
	 * @param {any} callback
	 */
	hook(name, callback) {
		const parts = name.split(':');
		const key = parts[0];
		// eslint-disable-next-line no-param-reassign
		name = (parts.length > 1) ? parts[1] : key;

		if (!hooks[name]) {
			hooks[name] = {};
		}
		hooks[name][key] = callback;
	},

	/**
	 * @param {{ kform: import('../kform.controller').default }} config
	 */
	init(config) {
		// eslint-disable-next-line no-underscore-dangle
		this._kform = config.kform;
	},

	/**
	 * @param {any} data
	 * @returns {boolean}
	 */
	isEmpty(data) {
		return (Array.isArray(data) && data.length === 0)
			|| data === undefined
			// @ts-ignore
			|| (typeof data === 'string' && data.trim() === '')
			|| data === null;
	},

	/**
	 * Check if a component is required
	 *
	 * @param {any} component
	 * @param {any[]} [componentsToRequire]
	 * @returns {boolean}
	 */
	isRequired(component, componentsToRequire = undefined) {
		if (component.validate?.card && (
			typeof component.validate?.card === 'string' || !(component.validate?.card instanceof Card))
		) {
			component.validate.card = new Card(component.validate.card);
		}
		return component.validate?.card?.isRequired() || (
			Array.isArray(componentsToRequire) && componentsToRequire.includes(component.key)
		);
	},

	/**
	 * @param {any} component
	 * @param {any} row
	 * @param {any} data
	 * @param {any[]} hide
	 * @param {any} form
	 * @returns {boolean}
	 */
	isVisible(component, row, data, hide, form) {
		// If the component is in the hideComponents array, then hide it by default.
		if (hide && Array.isArray(hide) && (hide.includes(component.key))) {
			return false;
		}

		return this.checkVisible(component, row, data, form);
	},

	/**
	 * @param {Function} f
	 * @returns {function(*): *}
	 */
	labelPositionWrapper(f) {
		return (/** @type {any} */ component) => f(component.labelPosition);
	},

	/**
	 * @param {any} value
	 * @param {any[]} inputMask
	 * @returns {boolean}
	 */
	matchInputMask(value, inputMask) {
		if (!inputMask) {
			return true;
		}
		for (const [index, charPart] of inputMask.entries()) {
			const char = value[index];

			if (!((_.isRegExp(charPart) && charPart.test(char)) || charPart === char)) {
				return false;
			}
		}

		return true;
	},

	/**
	 * @param {Function} function_
	 * @returns {any}
	 */
	optionsLabelPositionWrapper(function_) {
		return (/** @type {any} */ component) => function_(component.optionsLabelPosition);
	},

	parseFloat: Number.parseFloat,

	/**
	 * @param {string | any[]} defaultItems
	 * @param {any} searchItems
	 * @returns {any[]}
	 */
	pluckItems(defaultItems, searchItems) {
		/** @type {any[]} */
		let temporary = [];
		if (!defaultItems || defaultItems.length === 0) {
			return temporary;
		}
		if (typeof defaultItems === 'string') {
			// eslint-disable-next-line no-param-reassign
			defaultItems = [defaultItems];
		}

		for (const item of defaultItems) {
			const parts = item.split(':');
			if (parts.length === 2) {
				// eslint-disable-next-line angular/function-type
				const result = _.filter(searchItems, potential => _.get(potential, parts[0]) === parts[1]);

				if (result) {
					temporary = [...temporary, ...result];
				}
			}
		}

		return temporary;
	},

	jsonLogic,

	/**
	 * jsonLogic: formioUtils.jsonLogic
	 *
	 * @param {string} position
	 * @returns {boolean}
	 */
	topOrLeftOptionLabel(position) {
		return [
			'top',
			'left',
		].includes(position);
	},

	/**
	 * @param {string} input
	 * @param {any[]} mask
	 * @returns {any}
	 */
	verifyMaskedInput(input, mask) {
		// eslint-disable-next-line no-param-reassign
		input = conformToMask(input, mask).conformedValue;
		if (!this.matchInputMask(input, mask)) {
			return '';
		}
		return input;
	},
};

export default kformUtils;
